import { navigateToLogout } from '~/utils/navigateToLogin'
import _ from 'lodash'
import type { LocationForCurrentUserResponse, UserGroupsContext } from '~/clients'
import type { RemovableRef } from '@vueuse/core'
import type { PagedResponse } from '~/types'

const roleProtectedPortalRoles = [
  AuthRole.QRAY_GMP,
  AuthRole.QRAY_FSP,
  AuthRole.QRAY_MON,
  AuthRole.PORTAL_COMPANY,
]

export const useAuthStore = defineStore('auth', {
  state: () => ({
    id: '0000-0000-0000-0000',
    crmContactId: '' as string | undefined,
    name: 'user',
    roles: ['GUEST'] as string[],
    isLoggedIn: false,
    location: null as number | null | RemovableRef<number>,
    groups: [] as UserGroupsContext[],
    userLocations: [] as number[],
    correlationId: null as string | null,
    agreementAccepted: true, // default to true, so the popup is not shown on first load
    alreadyAgreed: false,
    timeZoneId: undefined as string | undefined,
  }),
  getters: {
    isEmployee({ roles }) {
      return roles.includes(AuthRole.QRAY_GMP)
    },
    isUserLoaded({ isLoggedIn, id }) {
      return isLoggedIn && id !== '0000-0000-0000-0000'
    },
    initials({ name }) {
      return getNameInitials(name)
    },
  },
  actions: {
    async logout() {
      this.correlationId = null
      this.isLoggedIn = false
      return navigateToLogout()
    },
    updateAgreementAccepted(value: boolean) {
      this.agreementAccepted = value
    },
    setAlreadyAgreed(value: boolean) {
      this.alreadyAgreed = value
    },
    adminGroups() {
      return this.groups.filter(
        (group) => group.typeName === 'Manager' && group.userCanBeGroupManager,
      )
    },
    setCorrelationId() {
      const id = crypto.randomUUID()
      this.correlationId = id
    },
    isHavingAdminGroups() {
      return this.adminGroups().length > 0
    },
    isInRoles(values: string[]) {
      if (values.length === 0) return true

      // ALWAYS allow QRAY_GMP, might change in the future to better role
      if (this.roles.includes(AuthRole.QRAY_GMP)) return true

      return _.some(values, (x) => {
        return this.roles.includes(x)
      })
    },
    isAuthorized(auth?: { roles?: string[]; requiresAdminGroups?: boolean }) {
      if (!auth) return true
      if (!this.isInRoles(auth.roles ?? [])) return false
      if (auth.requiresAdminGroups && !this.isHavingAdminGroups()) return false
      return true
    },
    isHavingCorrectRoles() {
      return (
        this.roles?.length > 0 &&
        this.roles.some((role) => Object.values(AuthRole).includes(role as AuthRole)) === true
      )
    },
    canUseRoleProtectedPortalFeatures() {
      return (
        this.roles?.length > 0 &&
        this.roles.some((role) => roleProtectedPortalRoles.includes(role as AuthRole)) === true
      )
    },
    isInLocations(value: number) {
      return this.userLocations?.length > 0 && this.userLocations.includes(value)
    },
    async fetchLoggedInUser() {
      const client = useApi()
      const result = await client.readCurrentUser()
      this.setCorrelationId() // generate and set ID to be used on all API request header (X-Correlation-ID)
      this.name = result.name
      this.id = result.objectId
      this.roles = result.roles
      this.groups = result.groups
      this.userLocations = result.userLocationIds
      this.crmContactId = result.crmContactId
      this.timeZoneId = result.timeZoneId
      setTimeZonePreference(result.timeZoneId)
      const locations = await useApiClient<PagedResponse<LocationForCurrentUserResponse>>(
        '/users/current/locations',
        {
          retry: 2,
        },
      )

      if (
        localStorage.getItem(`defaultLocation.${this.id ?? '-1'}`) &&
        !locations.values.some((s) => s.id === this.location)
      ) {
        localStorage.removeItem(`defaultLocation.${this.id ?? '-1'}`) // delete stored location when it's not in list of locations
      }

      if (!localStorage.getItem(`defaultLocation.${this.id ?? '-1'}`) || !this.location) {
        this.location = useLocalStorage(
          `defaultLocation.${this.id}`,
          locations.values?.[0]?.id,
        ).value // Todo: check if we have locations and check what we need to do if we don't have locations?
      }

      this.isLoggedIn = true
    },
  },
  persist: {
    pick: ['correlationId', 'agreementAccepted', 'alreadyAgreed'],
  },
})
